import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getChampionshipDetails, getChampionshipPointsConfig, updatePointsConfig } from '../services/api';
import '../styles/fonts.css';
import '../styles/organizer.css';

const SESSION_KEY = (id) => `organizer_auth_${id}`;
const PWD_KEY = (id) => `organizer_pwd_${id}`;

const NUM_CAT_OPTIONS = [1, 2, 3, 4, 5];
const PLACEHOLDER_NAMES = ['PRO', 'PROAM', 'AM', 'Bronze', 'Silver'];

function buildDefaultRows(n = 10) {
    return Array.from({ length: n }, (_, i) => ({ pos: i + 1, pts: '' }));
}

function buildDefaultHeats() {
    return [
        { name: 'HEAT 1', rows: buildDefaultRows(10) },
        { name: 'FEATURE', rows: buildDefaultRows(10) },
    ];
}

function OrganizerPointsConfig() {
    const { id, champId } = useParams();
    const { t } = useTranslation();
    const [champName, setChampName] = useState('');
    const [isMultiRace, setIsMultiRace] = useState(false);
    const [heats, setHeats] = useState(buildDefaultHeats());
    const [rows, setRows] = useState(buildDefaultRows(10));
    const [bonus, setBonus] = useState({
        pole: { enabled: false, pts: 1 },
        qualy_p2: { enabled: false, pts: 1 },
        qualy_p3: { enabled: false, pts: 1 },
        most_laps_led: { enabled: false, pts: 2 },
        any_lap_led: { enabled: false, pts: 1 },
        fastest_lap: { enabled: false, pts: 1 },
    });
    // Drops
    const [numDropsApi, setNumDropsApi] = useState(0);
    const [customNumDrops, setCustomNumDrops] = useState('');
    const [penaltyRacesDroppable, setPenaltyRacesDroppable] = useState(true);
    // Teams
    const [teamsList, setTeamsList] = useState(['']);
    const [driverTeams, setDriverTeams] = useState({});
    const [teamScoringUsesDriverDrops, setTeamScoringUsesDriverDrops] = useState(true);
    const [teamScoringUsesOverallPosition, setTeamScoringUsesOverallPosition] = useState(false);
    // Driver categories
    const [numCategories, setNumCategories] = useState(1);
    const [categoryNamesList, setCategoryNamesList] = useState(['']);
    const [driverCategories, setDriverCategories] = useState({});
    const [allDrivers, setAllDrivers] = useState([]);
    const [scoringByClass, setScoringByClass] = useState(false);

    // Outros
    const [regulationText, setRegulationText] = useState('');
    const [protestWindowDays, setProtestWindowDays] = useState('');
    // Campos de edição básica (só ligas manuais)
    const [isManual, setIsManual] = useState(false);
    const [champActive, setChampActive] = useState('true');
    const [totalRaces, setTotalRaces] = useState('');
    const [trackCategory, setTrackCategory] = useState('');
    const [seasonYear, setSeasonYear] = useState('');
    const [seasonQuarter, setSeasonQuarter] = useState('');
    // UI state
    const [activeTab, setActiveTab] = useState('cats');
    const [isDirty, setIsDirty] = useState(false);
    const dirtyEnabled = useRef(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const isAuth = sessionStorage.getItem(SESSION_KEY(id)) === 'true';
    const password = sessionStorage.getItem(PWD_KEY(id)) || '';

    const BONUS_DEFS = [
        { key: 'pole', label: t('organizer.bonusPoleLabel'), desc: t('organizer.bonusPoleDesc') },
        { key: 'qualy_p2', label: t('organizer.bonusQualyP2Label'), desc: t('organizer.bonusQualyP2Desc') },
        { key: 'qualy_p3', label: t('organizer.bonusQualyP3Label'), desc: t('organizer.bonusQualyP3Desc') },
        { key: 'most_laps_led', label: t('organizer.bonusMostLapsLedLabel'), desc: t('organizer.bonusMostLapsLedDesc') },
        { key: 'any_lap_led', label: t('organizer.bonusAnyLapLedLabel'), desc: t('organizer.bonusAnyLapLedDesc') },
        { key: 'fastest_lap', label: t('organizer.bonusFastestLapLabel'), desc: t('organizer.bonusFastestLapDesc') },
    ];

    useEffect(() => {
        Promise.all([
            getChampionshipDetails(champId),
            getChampionshipPointsConfig(champId),
        ])
            .then(([champData, configData]) => {
                setChampName(champData.name);
                setNumDropsApi(configData.num_drops || 0);

                // Points table
                const cp = configData.custom_points || {};
                const cb = configData.custom_bonus || {};
                const firstVal = Object.values(cp)[0];
                const nested = Object.keys(cp).length > 0 && firstVal !== null && typeof firstVal === 'object';
                if (nested) {
                    setIsMultiRace(true);
                    const loadedHeats = Object.entries(cp).map(([heatName, heatTable]) => {
                        const maxPos = Math.max(...Object.keys(heatTable).map(Number).filter(n => !isNaN(n)), 10);
                        const heatRows = Array.from({ length: maxPos }, (_, i) => ({
                            pos: i + 1,
                            pts: heatTable[String(i + 1)] !== undefined ? String(heatTable[String(i + 1)]) : '',
                        }));
                        return { name: heatName, rows: heatRows };
                    });
                    setHeats(loadedHeats);
                } else if (Object.keys(cp).length > 0) {
                    const maxPos = Math.max(...Object.keys(cp).map(Number).filter(n => !isNaN(n)), 10);
                    const newRows = Array.from({ length: maxPos }, (_, i) => ({
                        pos: i + 1,
                        pts: cp[String(i + 1)] !== undefined ? String(cp[String(i + 1)]) : '',
                    }));
                    setRows(newRows);
                }

                // Bonus
                setBonus({
                    pole: { enabled: 'pole' in cb, pts: cb.pole ?? 1 },
                    qualy_p2: { enabled: 'qualy_p2' in cb, pts: cb.qualy_p2 ?? 1 },
                    qualy_p3: { enabled: 'qualy_p3' in cb, pts: cb.qualy_p3 ?? 1 },
                    most_laps_led: { enabled: 'most_laps_led' in cb, pts: cb.most_laps_led ?? 2 },
                    any_lap_led: { enabled: 'any_lap_led' in cb, pts: cb.any_lap_led ?? 1 },
                    fastest_lap: { enabled: 'fastest_lap' in cb, pts: cb.fastest_lap ?? 1 },
                });

                // Drops
                const cdn = configData.custom_num_drops;
                const apiDrops = configData.num_drops || 0;
                setCustomNumDrops(cdn !== null && cdn !== undefined ? String(cdn) : (apiDrops > 0 ? String(apiDrops) : ''));
                setPenaltyRacesDroppable(configData.penalty_races_droppable !== false);

                // Teams
                const savedTeams = Array.isArray(configData.teams) && configData.teams.length > 0
                    ? configData.teams
                    : [''];
                setTeamsList(savedTeams);
                setDriverTeams(configData.driver_teams || {});
                setTeamScoringUsesDriverDrops(configData.team_scoring_uses_driver_drops !== false);
                setTeamScoringUsesOverallPosition(configData.team_scoring_uses_overall_position === true);

                // Driver categories
                const catNames = Array.isArray(configData.category_names) ? configData.category_names : [];
                const n = catNames.length >= 2 ? catNames.length : 1;
                setNumCategories(n);
                const padded = Array.from({ length: Math.max(n, 1) }, (_, i) => catNames[i] || '');
                setCategoryNamesList(padded);
                setDriverCategories(configData.driver_categories || {});
                setAllDrivers(configData.all_championship_drivers || []);
                setScoringByClass(configData.scoring_by_class || false);
                setRegulationText(configData.regulation_text || '');
                setProtestWindowDays(configData.protest_window_days != null ? String(configData.protest_window_days) : '');
                setTrackCategory(configData.track_category || '');
                setSeasonYear(configData.season_year != null ? String(configData.season_year) : '');
                setSeasonQuarter(configData.season_quarter != null ? String(configData.season_quarter) : '');
                setIsManual(!!configData.is_manual);
                setChampName(configData.name || '');
                setChampActive(configData.active !== false ? 'true' : 'false');
                setTotalRaces(configData.total_races != null ? String(configData.total_races) : '');
                setIsDirty(false);
                setTimeout(() => { dirtyEnabled.current = true; }, 100);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [champId]);

    // ── handlers ──────────────────────────────────────────────────────────────

    const handleRowChange = (index, value) => {
        setRows(prev => prev.map((r, i) => i === index ? { ...r, pts: value } : r));
    };

    const handleHeatNameChange = (hi, value) => {
        setHeats(prev => prev.map((h, i) => i === hi ? { ...h, name: value } : h));
    };
    const handleHeatRowChange = (hi, ri, value) => {
        setHeats(prev => prev.map((h, i) => i === hi
            ? { ...h, rows: h.rows.map((r, j) => j === ri ? { ...r, pts: value } : r) }
            : h
        ));
    };
    const addHeatRow = (hi) => {
        setHeats(prev => prev.map((h, i) => i === hi
            ? { ...h, rows: [...h.rows, { pos: h.rows.length + 1, pts: '' }] }
            : h
        ));
    };
    const removeHeatRow = (hi) => {
        setHeats(prev => prev.map((h, i) => i === hi && h.rows.length > 1
            ? { ...h, rows: h.rows.slice(0, -1) }
            : h
        ));
    };
    const addHeat = () => setHeats(prev => [...prev, { name: `${t('organizer.heat')} ${prev.length + 1}`, rows: buildDefaultRows(10) }]);
    const removeHeat = (hi) => setHeats(prev => prev.filter((_, i) => i !== hi));

    const handleNumCategoriesChange = (n) => {
        setNumCategories(n);
        setCategoryNamesList(prev => {
            const copy = [...prev];
            while (copy.length < n) copy.push('');
            return copy.slice(0, n);
        });
        if (n === 1) setScoringByClass(false);
    };

    const handleCategoryNameChange = (index, value) => {
        setCategoryNamesList(prev => prev.map((v, i) => i === index ? value : v));
    };

    const handleDriverCategory = (custId, category) => {
        setDriverCategories(prev => ({ ...prev, [String(custId)]: category }));
    };

    const addTeam = () => setTeamsList(prev => [...prev, '']);
    const removeTeam = (index) => setTeamsList(prev => prev.filter((_, i) => i !== index));
    const handleTeamName = (index, value) => setTeamsList(prev => prev.map((v, i) => i === index ? value : v));
    const handleDriverTeam = (custId, team) => setDriverTeams(prev => ({ ...prev, [String(custId)]: team }));

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');

        let custom_points = {};
        if (isMultiRace) {
            heats.forEach(heat => {
                if (!heat.name.trim()) return;
                const heatTable = {};
                heat.rows.forEach(r => {
                    const n = parseFloat(r.pts);
                    if (!isNaN(n) && n !== 0) heatTable[String(r.pos)] = n;
                });
                if (Object.keys(heatTable).length > 0) custom_points[heat.name.trim()] = heatTable;
            });
        } else {
            rows.forEach(r => {
                const n = parseFloat(r.pts);
                if (!isNaN(n) && n !== 0) custom_points[String(r.pos)] = n;
            });
        }

        const custom_bonus = {};
        BONUS_DEFS.forEach(({ key }) => {
            if (bonus[key].enabled) {
                const n = parseFloat(bonus[key].pts);
                custom_bonus[key] = isNaN(n) ? 0 : n;
            }
        });

        const customNumDropsVal = customNumDrops === '' ? null : parseInt(customNumDrops, 10);

        const category_names = numCategories >= 2
            ? categoryNamesList.map(n => n.trim())
            : [];
        const driver_categories = numCategories >= 2 ? driverCategories : {};

        const teams = teamsList.map(t => t.trim()).filter(t => t !== '');

        try {
            await updatePointsConfig(champId, password, {
                custom_points,
                custom_bonus,
                penalty_races_droppable: penaltyRacesDroppable,
                custom_num_drops: isNaN(customNumDropsVal) ? null : customNumDropsVal,
                scoring_by_class: numCategories >= 2 ? scoringByClass : false,
                category_names,
                driver_categories,
                teams,
                driver_teams: driverTeams,
                team_scoring_uses_driver_drops: teamScoringUsesDriverDrops,
                team_scoring_uses_overall_position: teamScoringUsesOverallPosition,
                regulation_text: regulationText,
                protest_window_days: protestWindowDays === '' ? null : parseInt(protestWindowDays, 10),
                track_category: trackCategory || null,
                season_year: seasonYear === '' ? null : parseInt(seasonYear, 10),
                season_quarter: seasonQuarter === '' ? null : parseInt(seasonQuarter, 10),
                ...(isManual && {
                    name: champName,
                    active: champActive,
                    total_races: totalRaces === '' ? null : parseInt(totalRaces, 10),
                }),
            });
            setIsDirty(false);
            setSuccess(t('organizer.configSaved'));
        } catch (e) {
            setError(e?.error || t('organizer.saveErrorRetry'));
        } finally {
            setSaving(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!dirtyEnabled.current) return;
        setIsDirty(true);
    }, [rows, heats, bonus, customNumDrops, penaltyRacesDroppable,
        numCategories, categoryNamesList, driverCategories, scoringByClass,
        teamsList, driverTeams, teamScoringUsesDriverDrops, teamScoringUsesOverallPosition,
        regulationText, protestWindowDays, trackCategory, seasonYear, seasonQuarter, isMultiRace]);

    const handleTabChange = (tab) => {
        if (isDirty) {
            const ok = window.confirm(t('organizer.unsavedChangesAlert'));
            if (!ok) return;
            setIsDirty(false);
        }
        setActiveTab(tab);
        setError('');
        setSuccess('');
    };

    const TABS = [
        { key: 'cats',   label: t('organizer.tabCats') },
        { key: 'points', label: t('organizer.tabPoints') },
        { key: 'bonus',  label: t('organizer.tabBonus') },
        { key: 'outros', label: t('organizer.tabOthers') },
    ];

    // ── render guards ─────────────────────────────────────────────────────────

    if (!isAuth) {
        return (
            <div className="OrganizerPage">
                <Header />
                <main>
                    <div className="organizer-content">
                        <p className="organizer-error">
                            {t('organizer.restrictedAccess')}{' '}
                            <Link to={`/leagues/${id}/organizer`}>{t('organizer.loginPanel')}</Link>
                        </p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (loading) return (
        <div className="OrganizerPage">
            <Header />
            <main><div className="organizer-content"><p className="organizer-loading">{t('common.loading')}</p></div></main>
            <Footer />
        </div>
    );

    const effectiveDrops = customNumDrops !== '' ? parseInt(customNumDrops, 10) : numDropsApi;
    const definedCats = numCategories >= 2
        ? categoryNamesList.map((n, i) => n.trim() || t('organizer.categoryN', { n: i + 1 }))
        : [];
    const definedTeams = teamsList.filter(t => t.trim() !== '');
    const showDriverTable = allDrivers.length > 0 && (numCategories >= 2 || definedTeams.length > 0);

    return (
        <div className="OrganizerPage">
            <Header />
            <main>
                <div className="organizer-content">

                    <div className="organizer-panel-header" style={{ marginBottom: 28 }}>
                        <div>
                            <p className="organizer-panel-label">
                                <Link
                                    to={`/leagues/${id}/organizer/championships/${champId}`}
                                    className="organizer-back-link"
                                >
                                    ← {champName}
                                </Link>
                            </p>
                            <h1 className="organizer-panel-name">{t('organizer.configureChamp')}</h1>
                        </div>
                    </div>

                    <div className="config-tabs" style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 0 }}>
                        {TABS.map(tab => (
                            <button key={tab.key} type="button"
                                style={{
                                    padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
                                    color: activeTab === tab.key ? 'var(--accent, #e8c84a)' : 'rgba(255,255,255,0.55)',
                                    borderBottom: activeTab === tab.key ? '2px solid var(--accent, #e8c84a)' : '2px solid transparent',
                                    fontWeight: activeTab === tab.key ? 600 : 400,
                                    fontSize: '0.95rem', marginBottom: -1, transition: 'color 0.15s',
                                }}
                                onClick={() => handleTabChange(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {activeTab === 'cats' && (<>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
                    {/* Driver categories */}
                    <div className="organizer-section" style={{ flex: 2, minWidth: 0, marginBottom: 0 }}>
                        <div className="box-header">
                            <h3>{t('organizer.categoriesSection')}</h3>
                        </div>
                        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
                            <div>
                                <label className="form-label" style={{ marginBottom: 8 }}>
                                    {t('organizer.howManyCategories')}
                                </label>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {NUM_CAT_OPTIONS.map(n => (
                                        <button
                                            key={n}
                                            type="button"
                                            className={numCategories === n ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
                                            onClick={() => handleNumCategoriesChange(n)}
                                        >
                                            {n === 1 ? t('organizer.noDivision') : n}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {numCategories >= 2 && (
                                <>
                                    <div>
                                        <p className="form-label" style={{ marginBottom: 10 }}>
                                            {t('organizer.nameEachCategory')}
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {Array.from({ length: numCategories }, (_, i) => (
                                                <label key={i} className="form-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 0 }}>
                                                    <span style={{ minWidth: 90, opacity: 0.5, fontSize: '0.85rem' }}>
                                                        {t('organizer.categoryN', { n: i + 1 })}
                                                    </span>
                                                    <input
                                                        type="text"
                                                        className="form-input"
                                                        style={{ maxWidth: 200 }}
                                                        value={categoryNamesList[i] || ''}
                                                        onChange={e => handleCategoryNameChange(i, e.target.value)}
                                                        placeholder={`${t('organizer.eg')} ${PLACEHOLDER_NAMES[i] || `Cat ${i + 1}`}`}
                                                    />
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <label className="bonus-toggle" style={{ cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={scoringByClass}
                                            onChange={e => setScoringByClass(e.target.checked)}
                                        />
                                        <span className="bonus-label">{t('organizer.scoringByClass')}</span>
                                        <span className="bonus-desc">
                                            {t('organizer.scoringByClassHint')}
                                        </span>
                                    </label>
                                </>
                            )}
                            {showDriverTable && (
                                <div>
                                    <p className="form-label" style={{ marginBottom: 10 }}>
                                        {numCategories >= 2 && definedTeams.length > 0
                                            ? t('organizer.assignCategoryAndTeam')
                                            : numCategories >= 2
                                                ? t('organizer.assignCategory')
                                                : t('organizer.assignTeam')}
                                    </p>
                                    {numCategories >= 2 && allDrivers.some(d => !driverCategories[String(d.cust_id)]) && (
                                        <p className="form-hint-block" style={{ marginBottom: 10 }}>
                                            {t('organizer.noCategoryWarning')}
                                        </p>
                                    )}
                                    <div className="penalty-table-scroll">
                                        <table className="points-config-table penalty-drivers-table">
                                            <thead>
                                                <tr>
                                                    <th className="col-driver">{t('common.driver')}</th>
                                                    {numCategories >= 2 && <th className="col-penalty-pts">{t('organizer.categoriesSection')}</th>}
                                                    {definedTeams.length > 0 && <th className="col-team">{t('common.team')}</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {allDrivers.map(driver => {
                                                    const assignedCat = driverCategories[String(driver.cust_id)] || '';
                                                    const assignedTeam = driverTeams[String(driver.cust_id)] || '';
                                                    return (
                                                        <tr key={driver.cust_id} className={numCategories >= 2 && !assignedCat ? 'penalty-row-unassigned' : ''}>
                                                            <td className="col-driver">{driver.display_name}</td>
                                                            {numCategories >= 2 && (
                                                                <td className="col-penalty-pts">
                                                                    <select
                                                                        className="form-input"
                                                                        value={assignedCat}
                                                                        onChange={e => handleDriverCategory(driver.cust_id, e.target.value)}
                                                                    >
                                                                        <option value="">{t('organizer.noCategoryOption')}</option>
                                                                        {definedCats.map((cat, i) => (
                                                                            <option key={i} value={cat}>{cat}</option>
                                                                        ))}
                                                                    </select>
                                                                </td>
                                                            )}
                                                            {definedTeams.length > 0 && (
                                                                <td className="col-team">
                                                                    <select
                                                                        className="form-input"
                                                                        value={assignedTeam}
                                                                        onChange={e => handleDriverTeam(driver.cust_id, e.target.value)}
                                                                    >
                                                                        <option value="">{t('organizer.noTeamOption')}</option>
                                                                        {definedTeams.map((team, i) => (
                                                                            <option key={i} value={team}>{team}</option>
                                                                        ))}
                                                                    </select>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {allDrivers.length === 0 && (numCategories >= 2 || definedTeams.length > 0) && (
                                <p className="form-hint-block">
                                    {t('organizer.noDriversFound')}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Teams */}
                    <div className="organizer-section" style={{ flex: 1, minWidth: 0, marginBottom: 0 }}>
                        <div className="box-header">
                            <h3>{t('organizer.teamsSection')}</h3>
                        </div>
                        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <p className="organizer-subtitle" style={{ marginBottom: 4 }}>
                                {t('organizer.teamsSubtitle')}
                            </p>
                            <label className="bonus-toggle" style={{ cursor: 'pointer', marginBottom: 4 }}>
                                <input
                                    type="checkbox"
                                    checked={teamScoringUsesDriverDrops}
                                    onChange={e => setTeamScoringUsesDriverDrops(e.target.checked)}
                                />
                                <span className="bonus-label">{t('organizer.teamScoresDrops')}</span>
                                <span className="bonus-desc">
                                    {t('organizer.teamScoresDropsHint')}
                                </span>
                            </label>
                            {numCategories >= 2 && (
                                <label className="bonus-toggle" style={{ cursor: 'pointer', marginBottom: 4 }}>
                                    <input
                                        type="checkbox"
                                        checked={teamScoringUsesOverallPosition}
                                        onChange={e => setTeamScoringUsesOverallPosition(e.target.checked)}
                                    />
                                    <span className="bonus-label">{t('organizer.teamScoresOverall')}</span>
                                    <span className="bonus-desc">
                                        {t('organizer.teamScoresOverallHint')}
                                    </span>
                                </label>
                            )}
                            {teamsList.map((team, i) => (
                                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        className="form-input"
                                        style={{ maxWidth: 260 }}
                                        value={team}
                                        onChange={e => handleTeamName(i, e.target.value)}
                                        placeholder={t('organizer.teamNamePlaceholder', { n: i + 1 })}
                                    />
                                    {teamsList.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn-icon-remove"
                                            onClick={() => removeTeam(i)}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            ))}
                            <div style={{ marginTop: 4 }}>
                                <button type="button" className="btn-secondary btn-sm" onClick={addTeam}>
                                    {t('organizer.addTeam')}
                                </button>
                            </div>
                        </div>
                    </div>
                    </div>
                    {error && <p className="form-error" style={{ marginBottom: 12 }}>{error}</p>}
                    {success && <p className="form-success" style={{ marginBottom: 12 }}>{success}</p>}
                    <button className="btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? t('common.saving') : t('organizer.saveConfig')}
                    </button>
                    </>)}

                    {activeTab === 'points' && (<>
                    {/* Points by position */}
                    <div className="organizer-section" style={{ marginBottom: 16 }}>
                        <div className="box-header">
                            <h3>{t('organizer.pointsByPosition')}</h3>
                        </div>
                        <div style={{ padding: '16px 20px' }}>

                            {/* Mode: single race or multi-heat */}
                            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                                <button
                                    type="button"
                                    className={!isMultiRace ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
                                    onClick={() => setIsMultiRace(false)}
                                >
                                    {t('organizer.singleRace')}
                                </button>
                                <button
                                    type="button"
                                    className={isMultiRace ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
                                    onClick={() => setIsMultiRace(true)}
                                >
                                    {t('organizer.multiHeat')}
                                </button>
                            </div>

                            {isMultiRace ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                                    {heats.map((heat, hi) => {
                                        const COL_SIZE = 15;
                                        const heatChunks = [];
                                        for (let i = 0; i < heat.rows.length; i += COL_SIZE)
                                            heatChunks.push(heat.rows.slice(i, i + COL_SIZE));
                                        return (
                                            <div key={hi} style={{ borderLeft: '3px solid rgba(255,255,255,0.12)', paddingLeft: 16 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                                    <input
                                                        type="text"
                                                        className="form-input"
                                                        style={{ maxWidth: 160, fontWeight: 600 }}
                                                        value={heat.name}
                                                        onChange={e => handleHeatNameChange(hi, e.target.value)}
                                                        placeholder={`${t('organizer.heat')} ${hi + 1}`}
                                                    />
                                                    <span className="bonus-desc">{t('organizer.heatNameHint')}</span>
                                                    {heats.length > 1 && (
                                                        <button type="button" className="btn-icon-remove" onClick={() => removeHeat(hi)}>{t('organizer.remove')}</button>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 10 }}>
                                                    {heatChunks.map((chunk, colIdx) => (
                                                        <table key={colIdx} className="points-config-table">
                                                            <thead><tr><th>{t('organizer.position')}</th><th>{t('common.points')}</th></tr></thead>
                                                            <tbody>
                                                                {chunk.map((row, ri) => {
                                                                    const globalRi = colIdx * COL_SIZE + ri;
                                                                    return (
                                                                        <tr key={globalRi}>
                                                                            <td>{row.pos}º</td>
                                                                            <td>
                                                                                <input
                                                                                    type="number" min="0" step="0.5"
                                                                                    className="form-input points-pts-input"
                                                                                    value={row.pts}
                                                                                    onChange={e => handleHeatRowChange(hi, globalRi, e.target.value)}
                                                                                    placeholder="0"
                                                                                />
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    ))}
                                                </div>
                                                <div className="points-config-actions">
                                                    <button className="btn-secondary btn-sm" type="button" onClick={() => addHeatRow(hi)}>{t('organizer.addPosition')}</button>
                                                    {heat.rows.length > 1 && (
                                                        <button className="btn-icon-remove" type="button" onClick={() => removeHeatRow(hi)}>{t('organizer.removeLast')}</button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div>
                                        <button type="button" className="btn-secondary btn-sm" onClick={addHeat}>{t('organizer.addHeat')}</button>
                                    </div>
                                </div>
                            ) : (() => {
                                const COL_SIZE = 15;
                                const chunks = [];
                                for (let i = 0; i < rows.length; i += COL_SIZE)
                                    chunks.push(rows.slice(i, i + COL_SIZE));
                                return (
                                    <>
                                        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 10 }}>
                                            {chunks.map((chunk, colIdx) => (
                                                <table key={colIdx} className="points-config-table">
                                                    <thead>
                                                        <tr>
                                                            <th>{t('organizer.position')}</th>
                                                            <th>{t('common.points')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {chunk.map((row, ri) => {
                                                            const i = colIdx * COL_SIZE + ri;
                                                            return (
                                                                <tr key={i}>
                                                                    <td>{row.pos}º</td>
                                                                    <td>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            step="0.5"
                                                                            className="form-input points-pts-input"
                                                                            value={row.pts}
                                                                            onChange={e => handleRowChange(i, e.target.value)}
                                                                            placeholder="0"
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            ))}
                                        </div>
                                        <div className="points-config-actions">
                                            <button className="btn-secondary btn-sm" onClick={() => setRows(prev => [...prev, { pos: prev.length + 1, pts: '' }])} type="button">
                                                {t('organizer.addPosition')}
                                            </button>
                                            {rows.length > 1 && (
                                                <button className="btn-icon-remove" onClick={() => setRows(prev => prev.slice(0, -1))} type="button">
                                                    {t('organizer.removeLast')}
                                                </button>
                                            )}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                    {error && <p className="form-error" style={{ marginBottom: 12 }}>{error}</p>}
                    {success && <p className="form-success" style={{ marginBottom: 12 }}>{success}</p>}
                    <button className="btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? t('common.saving') : t('organizer.saveConfig')}
                    </button>
                    </>)}

                    {activeTab === 'bonus' && (<>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
                    {/* Bonus */}
                    <div className="organizer-section" style={{ flex: 1, minWidth: 0, marginBottom: 0 }}>
                        <div className="box-header">
                            <h3>{t('organizer.bonusSection')}</h3>
                        </div>
                        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {BONUS_DEFS.map(({ key, label, desc }) => (
                                <div key={key} className="points-config-bonus">
                                    <label className="bonus-toggle">
                                        <input
                                            type="checkbox"
                                            checked={bonus[key].enabled}
                                            onChange={() => setBonus(prev => ({ ...prev, [key]: { ...prev[key], enabled: !prev[key].enabled } }))}
                                        />
                                        <span className="bonus-label">{label}</span>
                                        <span className="bonus-desc">{desc}</span>
                                    </label>
                                    {bonus[key].enabled && (
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.5"
                                            className="form-input points-pts-input"
                                            value={bonus[key].pts}
                                            onChange={e => setBonus(prev => ({ ...prev, [key]: { ...prev[key], pts: e.target.value } }))}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Drops */}
                    <div className="organizer-section" style={{ flex: 1, minWidth: 0, marginBottom: 0 }}>
                        <div className="box-header">
                            <h3>{t('organizer.dropsSection')}</h3>
                        </div>
                        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div>
                                <label className="form-label">
                                    {t('organizer.numDrops')}
                                    <span className="bonus-desc" style={{ marginLeft: 8 }}>
                                        {t('organizer.dropsHint', { count: numDropsApi })}
                                    </span>
                                    <input
                                        type="number"
                                        min="0"
                                        className="form-input points-pts-input"
                                        value={customNumDrops}
                                        onChange={e => setCustomNumDrops(e.target.value)}
                                        placeholder={String(numDropsApi)}
                                        style={{ marginTop: 8, width: 80 }}
                                    />
                                </label>
                                {effectiveDrops > 0 && (
                                    <p className="form-hint-block" style={{ marginTop: 8 }}>
                                        {t('organizer.dropsWillDropPre')} <strong>{effectiveDrops}</strong>{' '}
                                        {t('organizer.dropsWillDropPost')}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="bonus-toggle" style={{ cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={penaltyRacesDroppable}
                                        onChange={e => setPenaltyRacesDroppable(e.target.checked)}
                                    />
                                    <span className="bonus-label">{t('organizer.penaltyDroppable')}</span>
                                    <span className="bonus-desc">
                                        {t('organizer.penaltyDroppableHint')}
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                    </div>

                    {error && <p className="form-error" style={{ marginBottom: 12 }}>{error}</p>}
                    {success && <p className="form-success" style={{ marginBottom: 12 }}>{success}</p>}
                    <button className="btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? t('common.saving') : t('organizer.saveConfig')}
                    </button>
                    </>)}

                    {activeTab === 'outros' && (<>
                    <div className="organizer-section" style={{ marginBottom: 16 }}>
                        <div className="box-header">
                            <h3>{t('organizer.tabOthers')}</h3>
                        </div>
                        <div style={{ padding: '16px 20px' }}>

                        {isManual && (
                            <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                                <label className="form-label" style={{ flex: 2, minWidth: 220 }}>
                                    {t('organizer.champName')}
                                    <input
                                        type="text"
                                        className="form-input"
                                        style={{ marginTop: 6 }}
                                        value={champName}
                                        onChange={e => setChampName(e.target.value)}
                                    />
                                </label>
                                <label className="form-label" style={{ flex: 1, minWidth: 140 }}>
                                    {t('organizer.champStatus')}
                                    <select
                                        className="form-input"
                                        style={{ marginTop: 6 }}
                                        value={champActive}
                                        onChange={e => setChampActive(e.target.value)}
                                    >
                                        <option value="true">{t('organizer.champStatusActive')}</option>
                                        <option value="false">{t('organizer.champStatusFinished')}</option>
                                    </select>
                                </label>
                                <label className="form-label" style={{ flex: 1, minWidth: 120 }}>
                                    {t('organizer.champTotalRaces')}
                                    <input
                                        type="number"
                                        min="1"
                                        className="form-input"
                                        style={{ marginTop: 6 }}
                                        value={totalRaces}
                                        onChange={e => setTotalRaces(e.target.value)}
                                        placeholder="—"
                                    />
                                </label>
                            </div>
                        )}

                            <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                                <label className="form-label" style={{ flex: 1, minWidth: 180 }}>
                                    {t('organizer.trackCategoryLabel')}
                                    <select
                                        className="form-input"
                                        style={{ marginTop: 6 }}
                                        value={trackCategory}
                                        onChange={e => setTrackCategory(e.target.value)}
                                    >
                                        <option value="">{t('organizer.trackCategoryNone')}</option>
                                        <option value="Road">Road</option>
                                        <option value="Oval">Oval</option>
                                        <option value="Dirt Road">Dirt Road</option>
                                        <option value="Dirt Oval">Dirt Oval</option>
                                    </select>
                                </label>
                                <label className="form-label" style={{ flex: 1, minWidth: 120 }}>
                                    {t('organizer.seasonYearLabel')}
                                    <input
                                        type="number"
                                        min="2000"
                                        max="2100"
                                        step="1"
                                        className="form-input"
                                        style={{ marginTop: 6 }}
                                        value={seasonYear}
                                        onChange={e => setSeasonYear(e.target.value)}
                                        placeholder="2025"
                                    />
                                </label>
                                <label className="form-label" style={{ flex: 1, minWidth: 120 }}>
                                    {t('organizer.seasonQuarterLabel')}
                                    <select
                                        className="form-input"
                                        style={{ marginTop: 6 }}
                                        value={seasonQuarter}
                                        onChange={e => setSeasonQuarter(e.target.value)}
                                    >
                                        <option value="">{t('organizer.seasonQuarterNone')}</option>
                                        <option value="1">S1</option>
                                        <option value="2">S2</option>
                                        <option value="3">S3</option>
                                        <option value="4">S4</option>
                                    </select>
                                </label>
                            </div>
                            <label className="form-label" style={{ marginBottom: 20, display: 'block' }}>
                                {t('protest.protestWindowDaysLabel')}
                                <p className="organizer-subtitle" style={{ marginBottom: 6 }}>
                                    {t('protest.protestWindowDaysHint')}
                                </p>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    className="form-input points-pts-input"
                                    style={{ width: 100 }}
                                    value={protestWindowDays}
                                    onChange={e => setProtestWindowDays(e.target.value)}
                                    placeholder="—"
                                />
                            </label>
                            <p className="organizer-subtitle" style={{ marginBottom: 12 }}>
                                {t('organizer.regulationHint')}
                            </p>
                            <textarea
                                className="form-input"
                                style={{ width: '100%', minHeight: 340, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                                value={regulationText}
                                onChange={e => setRegulationText(e.target.value)}
                                placeholder={t('organizer.regulationPlaceholder')}
                            />
                        </div>
                    </div>
                    {error && <p className="form-error" style={{ marginBottom: 12 }}>{error}</p>}
                    {success && <p className="form-success" style={{ marginBottom: 12 }}>{success}</p>}
                    <button className="btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? t('common.saving') : t('organizer.saveConfig')}
                    </button>
                    </>)}

                </div>
            </main>
            <Footer />
        </div>
    );
}

export default OrganizerPointsConfig;
